import React, { useEffect, useState } from "react";
import { selectUser } from "../../../features/userSlice";
import { useSelector } from "react-redux";
import { getDeliveryRequest } from "../../../utilities";
import MaterialTable from "material-table";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import { ThemeProvider, Tooltip } from "@mui/material";
import RequestSnackbar from "../../../components/Other/RequestSnackbar";
import { icons } from "../../../components/Utilities/UtilitiesWD";
import { useAppMuiTheme } from "../../../theme/MuiThemeBridge";
import { PageHeader, PageShell } from "../../../components/UI/Page";
import { EmptyState, StatTile } from "../../../components/UI/States";
import {
  Pill,
  mtComponents,
  mtHeaderStyle,
  mtRowStyle,
  mtSearchFieldStyle,
  mtStyle,
} from "../../../components/WindowDoor/wdUi";

const Requests = () => {
  const user = useSelector(selectUser);
  const [dataStore, setDataStore] = useState([]);
  const defaultMaterialTheme = useAppMuiTheme();
  const [reminderFlag, setreminderFlag] = useState(false);

  const sendReminder = async (rowData) => {
    var data = JSON.stringify(rowData);

    const rawResponse = await fetch("/api/request/reminder", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: data,
    });
    const content = await rawResponse.json();
    setreminderFlag(true);
    getDeliveryRequest(user.email, setDataStore);
  };

  useEffect(() => {
    if (user) {
      getDeliveryRequest(user.email, setDataStore);
    }
  }, [user]);

  const requests = Array.isArray(dataStore) ? dataStore : [];
  const pendingCount = requests.filter((row) => row.requestStatus === 0).length;
  const acceptedCount = requests.filter((row) => row.requestStatus === 1).length;
  const rejectedCount = requests.filter((row) => row.requestStatus === 2).length;

  return (
    <PageShell size="2xl">
      <PageHeader
        icon={AssignmentOutlinedIcon}
        tone="indigo"
        eyebrow="Windows & Doors"
        title="My Requests"
        description="Delivery requests you've submitted and their status."
      />
      <div className="mb-6 grid grid-cols-3 gap-3 sm:max-w-xl">
        <StatTile label="Pending" value={pendingCount} tone="amber" />
        <StatTile label="Accepted" value={acceptedCount} tone="emerald" />
        <StatTile label="Rejected" value={rejectedCount} tone="rose" />
      </div>
      <div className="app-card overflow-hidden rounded-2xl border">
        <ThemeProvider theme={defaultMaterialTheme}>
          <MaterialTable
            style={mtStyle}
            columns={[
              {
                title: "Order#",
                field: "salesOrder",
                render: (rowData) => (
                  <span className="font-semibold tabular-nums">
                    {rowData.salesOrder}
                  </span>
                ),
              },
              {
                title: "Submit Date",
                field: "requestSubmitted",
                render: (rowData) => {
                  let rowDate = new Date(
                    rowData["requestSubmitted"]
                  ).toLocaleDateString("en-US");

                  return <p className="tabular-nums">{rowDate}</p>;
                },
              },
              {
                title: "Customer",
                field: "customer",
              },
              {
                title: "Job",
                field: "job",
              },
              {
                title: "Delivery Date Requested",
                field: "requestDate",
                render: (rowData) => {
                  if (rowData.earliestAvailable) {
                    return <Pill tone="sky">Earliest</Pill>;
                  } else {
                    return <p className="tabular-nums">{rowData.requestDate}</p>;
                  }
                },
              },
              {
                title: "Status",
                field: "requestStatus",
                render: (rowData) => {
                  //console.log(rowData.requestSent);

                  if (rowData.requestStatus === 0) {
                    if (typeof rowData.requestSent === "undefined") {
                      if (
                        (new Date() - new Date(rowData.createdAt)) /
                          1000 /
                          60 >
                        30
                      ) {
                        return (
                          <div className="flex items-center gap-1">
                            <Pill tone="amber">Pending</Pill>

                            <Tooltip title="Send reminder">
                              <button
                                type="button"
                                aria-label="Send reminder"
                                onClick={() => {
                                  sendReminder(rowData);
                                }}
                                className="app-icon-btn text-rose-600 hover:text-rose-700 dark:text-rose-400 dark:hover:text-rose-300"
                              >
                                <NotificationsActiveIcon fontSize="small" />
                              </button>
                            </Tooltip>
                          </div>
                        );
                      } else {
                        return <Pill tone="amber">Pending</Pill>;
                      }
                    } else {
                      return <Pill tone="amber">Pending</Pill>;
                    }
                  } else if (rowData.requestStatus === 1) {
                    if (rowData.earliestAvailable) {
                      return (
                        <Pill tone="emerald">
                          Scheduled For: {rowData?.earliestDateScheduled}
                        </Pill>
                      );
                    } else {
                      return <Pill tone="emerald">Accepted</Pill>;
                    }
                  } else if (rowData.requestStatus === 2) {
                    return <Pill tone="rose">Rejected</Pill>;
                  } else {
                    return <Pill>Unknown</Pill>;
                  }
                },
              },
            ]}
            data={dataStore.slice().reverse()}
            title=""
            icons={icons}
            components={{
              Action: (props) => (
                <button type="button" aria-label="Request delivery">
                  <LocalShippingIcon
                    onClick={(event) => {
                      props.action.onClick(event, props.data);
                    }}
                    color="primary"
                    variant="text"
                    style={{ textTransform: "none" }}
                    size="small"
                  />
                </button>
              ),
              ...mtComponents,
            }}
            options={{
              pageSize: 5, // make initial page size
              emptyRowsWhenPaging: false, // To avoid of having empty rows
              pageSizeOptions: [10, 15, 20, 25],
              headerStyle: { search: true, ...mtHeaderStyle },
              searchFieldStyle: mtSearchFieldStyle,
              rowStyle: mtRowStyle,
            }}
            localization={{
              body: {
                emptyDataSourceMessage: (
                  <EmptyState
                    icon={LocalShippingIcon}
                    title="No delivery requests"
                    description="Please Contact Helpdesk With Assistance In Retrieving Records"
                  />
                ),
              },
            }}
          />
        </ThemeProvider>
      </div>
      <RequestSnackbar
        open={reminderFlag}
        setOpen={setreminderFlag}
        message={`Reminder has been sent!`}
      />
    </PageShell>
  );
};

export default Requests;
